import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { isValidUuid } from "@/lib/utils";
import type { DeltaItem } from "@/components/walkthrough/since-last-run";
import type { WalkthroughFinding } from "./workspace-types";
import { buildFallbackRemoteFinding } from "./active-finding-fallback";

interface UseActiveFindingOptions {
  walkthrough: any;
  allFindings: WalkthroughFinding[];
  selectedFindingId: string | null;
  stepNumberFromUrl: number | null;
}

export function useActiveFinding({
  walkthrough,
  allFindings,
  selectedFindingId,
  stepNumberFromUrl,
}: UseActiveFindingOptions) {
  const [selectedDeltaItem, setSelectedDeltaItem] = useState<DeltaItem | null>(
    null,
  );

  const directMatch = useMemo(() => {
    if (!selectedFindingId && !selectedDeltaItem) return null;
    return (
      allFindings.find(
        (f) =>
          (selectedFindingId &&
            (f.id === selectedFindingId ||
              f.canonicalFindingId === selectedFindingId)) ||
          (selectedDeltaItem &&
            f.title &&
            f.title.trim() === selectedDeltaItem.title.trim()),
      ) ?? null
    );
  }, [allFindings, selectedFindingId, selectedDeltaItem]);

  const isRemoteLookup =
    !!selectedFindingId && !directMatch && isValidUuid(selectedFindingId);

  const { data: remoteFinding } = trpc.finding.getById.useQuery(
    { id: selectedFindingId! },
    { enabled: isRemoteLookup },
  );

  const fallbackRemoteFinding: WalkthroughFinding | null = useMemo(() => {
    return buildFallbackRemoteFinding({
      remoteFinding,
      walkthrough,
      selectedDeltaItem,
      selectedFindingId,
    });
  }, [remoteFinding, selectedDeltaItem, selectedFindingId, walkthrough]);

  const activeFinding: WalkthroughFinding | null =
    directMatch ?? fallbackRemoteFinding;

  const walkthroughSteps = (walkthrough?.steps ?? []) as Array<{
    id: string;
    stepNumber: number;
    title: string;
    personaReasoning: string | null;
    grounding: string | null;
    screenshotPath: string | null;
    pageUrl?: string | null;
    surface?: string | null;
  }>;

  const activeStep = useMemo(() => {
    if (!walkthrough) return null;

    if (activeFinding?.walkthroughStepId) {
      const local = walkthroughSteps.find(
        (s) => s.id === activeFinding.walkthroughStepId,
      );
      if (local) {
        return {
          id: local.id,
          stepNumber: local.stepNumber,
          title: local.title,
          walkthroughId: walkthrough.id,
          personaReasoning: local.personaReasoning,
          pageUrl: local.pageUrl,
        };
      }
    }

    if (activeFinding) {
      const remoteAny = remoteFinding as any;
      const resolvedStepNum =
        activeFinding.stepNumber ??
        activeFinding.steps?.[0] ??
        remoteAny?.effectiveStepNumber ??
        remoteAny?.stepNumber ??
        remoteAny?.evidenceStep?.stepNumber ??
        null;
      const resolvedWalkthroughId =
        activeFinding.walkthroughId ??
        remoteAny?.effectiveWalkthroughId ??
        remoteAny?.walkthroughId ??
        walkthrough.id;

      if (typeof resolvedStepNum === "number") {
        const local = walkthroughSteps.find(
          (s) => s.stepNumber === resolvedStepNum,
        );
        return {
          id: local?.id ?? activeFinding.walkthroughStepId ?? "evidence-step",
          stepNumber: resolvedStepNum,
          title:
            local?.title ??
            remoteAny?.effectiveTitle ??
            remoteAny?.stepTitle ??
            remoteAny?.evidenceStep?.stepTitle ??
            `Step ${resolvedStepNum}`,
          walkthroughId: resolvedWalkthroughId,
          personaReasoning:
            local?.personaReasoning ?? remoteAny?.personaReasoning ?? null,
          pageUrl: local?.pageUrl ?? remoteAny?.pageUrl ?? null,
        };
      }
    }

    if (stepNumberFromUrl !== null) {
      const local = walkthroughSteps.find(
        (s) => s.stepNumber === stepNumberFromUrl,
      );
      if (local) {
        return {
          id: local.id,
          stepNumber: local.stepNumber,
          title: local.title,
          walkthroughId: walkthrough.id,
          personaReasoning: local.personaReasoning,
          pageUrl: local.pageUrl,
        };
      }
    }

    return null;
  }, [
    walkthrough,
    activeFinding,
    walkthroughSteps,
    remoteFinding,
    stepNumberFromUrl,
  ]);

  return {
    activeFinding,
    activeStep,
    selectedDeltaItem,
    setSelectedDeltaItem,
  };
}
